import { FunctionComponent, useEffect, useRef, useState } from "react";
import Table from "../../../Components/Table/Table";
import CustomToolbar from "../../../Components/Table/components/CustomToolbar";
import ButtonField from "../../../Components/Button/ButtonField";
import { Columns } from "../../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import TableSearch from "../../../Components/Table/components/TableSearch";
import IconButtonField from "../../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";

import { ReactComponent as Edit } from "../../../Assets/Images/edit.svg";
import { ReactComponent as RefreshButton } from "../../../Assets/Images/RefreshButton.svg";

import Dialog from "../../../Components/Dialog/Dialog";
import ResponseDialog from "../../../Components/ResponseDialog/ResponseDialog";
import { IClassSelectionMapping } from "./IClassSelection";
import ClassSelectionForm from "./ClassSelectionForm";
import { ClassSelectionMasterRead } from "../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionMasterRead";
import {
  ClassSelectionCreate_Update,
  clearClassSelectionCreate_Update,
} from "../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionMasterCreate_Update";
import DeleteDialog from "../../../Components/DeleteDialog/DeleteDialog";
import { IDeleteDialog } from "../../../Components/DeleteDialog/IDeleteDialog";
import { ReactComponent as Delete } from "../../../Assets/Images/delete.svg";

const ClassSelectionMaster: FunctionComponent = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteDialogData, setDeleteData] =
    useState<IClassSelectionMapping | null>(null);

  const [initialValues, setInitialValues] = useState<
    IClassSelectionMapping | {}
  >({
    status: true,
  });

  const formRef = useRef<HTMLFormElement>(null);
  const dispatch = useAppDispatch();

  const { data: classSelectionList } = useAppSelector(
    (state) => state.ClassSelectionMasterRead
  );
  const { data: createOrUpdateResponse } = useAppSelector(
    (state) => state.ClassSelectionCreate_Update
  );

  // Columns
  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      width: 80,
      headerAlign: "center",
      align: "center",
    },
{
  field: "date",
  headerName: "Date",
  flex: 1,
  align: "center",
  headerAlign: "center",
  renderCell: (params) => {
    const dates = params.row?.dates || [];

    if (!dates.length) return "-";

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {dates.map((d:any, i:any) => (
          <div
            key={i}
            style={{
              background: "#E3F2FD",
              color: "#1976D2",
              border: "1px solid #90CAF9",
              borderRadius: "9999px",
              padding: "4px 10px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              width: "fit-content",
              whiteSpace: "nowrap",
            }}
          >
            {d.date}
          </div>
        ))}
      </div>
    );
  },
},


    {
      field: "tutor_name",
      renderHeader: () => <b>Tutor</b>,
      flex: 1,
      type: "string",
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.tutor?.name || "-",
    },
    {
      field: "subject_name",
      renderHeader: () => <b>Subject</b>,
      flex: 1,
      type: "string",
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.subject?.name || "-",
    },
    {
      field: "center_name",
      renderHeader: () => <b>Center</b>,
      flex: 1,
      type: "string",
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.center?.center_name || "-",
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: (params) =>
        `activename ${params.row.status ? "active" : "inactive"}`,
      valueFormatter: (params) => (params.value ? "Active" : "Inactive"),
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: "actions",
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<IClassSelectionMapping>) => (
        <>
          <IconButtonField
            onClick={() => {
              const row = params.row;

              setInitialValues({
                ...row,
                tutor_id: row.tutor_id || row.tutor?.id || 0,
                subject_id: row.subject_id || row.subject?.id || 0,

                // ✅ FIX HERE — always pass center_id
                center_id: row.center_id || row.center?.center_id || "",

                dates: row.dates || [],
              });

              setDialogOpen(true);
              setIsEdit(true);
            }}
            disabled={dialogOpen}
          >
            <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
          </IconButtonField>
          <IconButtonField
            onClick={() => {
              dispatch(clearClassSelectionCreate_Update());
              setDeleteData({ ...params.row });
              setDeleteDialogOpen(true);
            }}
          >
            <SvgIcon component={Delete} inheritViewBox />
          </IconButtonField>
        </>
      ),
    },
  ];

  // Add index to rows
  const classSelections = Array.isArray(classSelectionList?.data)
    ? classSelectionList?.data.map((item, index) => ({
        ...item,
        index: index + 1,
      }))
    : [];

  useEffect(() => {
    dispatch(ClassSelectionMasterRead());
  }, [dispatch, createOrUpdateResponse]);

  useEffect(() => {
    return () => {
      dispatch(clearClassSelectionCreate_Update());
    };
  }, [dispatch]);

  return (
    <>
      {/* 👇 FIX: added getRowId to use class_selection_mapping_id */}
      <Table
        rows={classSelections ?? []}
        columns={columns}
        getRowId={(row) => row.class_selection_master_id}
      >
        <CustomToolbar>
          <h6>Class Selection</h6>
          <TableSearch />
          <ButtonField
            className="tabel_add_new_button"
            onClick={() => {
              setInitialValues({
                class_selection_master_id: "",
                day: "",
                status: true,
                tutor_id: 0,
                subject_id: 0,
                center_id: "",
              });
              setDialogOpen(true);
              setIsEdit(false);
            }}
            disabled={dialogOpen}
            toolTip="Add new"
          >
            Add new
          </ButtonField>

          <IconButtonField
            className="refresh_btn"
            toolTip="Refresh"
            onClick={() => dispatch(ClassSelectionMasterRead())}
          >
            <SvgIcon
              className="refresh_svg_icon"
              component={RefreshButton}
              inheritViewBox
            />
          </IconButtonField>
        </CustomToolbar>
      </Table>

      {/* Form Dialog */}
      <Dialog
        open={dialogOpen}
        dialogClassName="dialog_double_input"
        Dialog_Title={`${isEdit ? "Update" : "Add"} Class Selection`}
        Dialog_Content={
          <ClassSelectionForm
            formRef={formRef}
            initialValues={initialValues}
            setInitialValues={setInitialValues}
            isEdit={isEdit}
          />
        }
        Dialog_Actions={
          <>
            <ButtonField
              className="form_cancel_button"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </ButtonField>
            <ButtonField
              className="form_save_button"
              onClick={() => {
                formRef.current?.requestSubmit();
              }}
            >
              Save
            </ButtonField>
          </>
        }
      />

      <ResponseDialog
        okFunction={() => setDialogOpen(false)}
        response={createOrUpdateResponse}
        parentDialog={setDialogOpen}
      />

      {deleteDialogData && deleteDialogOpen && (
        <DeleteDialog
          deleteableName={""}
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
          deleteFunction={() => {
            if (deleteDialogData?.class_selection_master_id) {
              const payload = {
                class_selection_master_id:
                  deleteDialogData.class_selection_master_id,
                tutor_id:
                  deleteDialogData.tutor_id || deleteDialogData.tutor?.id || 0,
                subject_id:
                  deleteDialogData.subject_id ||
                  deleteDialogData.subject?.id ||
                  0,
                center_id:
                  deleteDialogData.center_id ||
                  deleteDialogData.center?.center_id ||
                  "",
                is_delete: true,
                status: deleteDialogData.status ?? true,
                date: new Date().toISOString(), // or use existing value if required
              };

              dispatch(ClassSelectionCreate_Update(payload));
            }
          }}
          response={createOrUpdateResponse}
        />
      )}
    </>
  );
};

export default ClassSelectionMaster;
